import { CometChannel, ICometChannel, ISocketLogger } from "geotab-web-sockets";
import WebSocketFactory from "../utils/webSocketFactory";
import WebSocketLogger  from "../utils/webSocketLogger";
import MyGeotabConnectionAdapter from "../utils/myGeotabConnectionAdapter";
import { SubscriptionsService } from "../services/base";
import Api from "./api";
import { uuid } from "geotab-uuid";
import { Source } from "../utils/logger";
import * as logger from "../utils/logger";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { IApiError } from "../utils/api.interfaces";

interface IDriveSocketLogger extends ISocketLogger {
    error(s: string, error?: Error | IApiError): void;
}

interface IWebSocketChannelError extends Error {
    data?: {
        id?: string;
    };
}

/** Pre-serialised drive-ping JSON frame sent as a liveness probe to the server. */
const DRIVE_PING_MESSAGE = "{\"type\":\"DrivePing\"}";

export default class WebSocketChannel extends SubscriptionsService {
    private readonly logger: IDriveSocketLogger;
    private readonly api: Api;
    private readonly user: string | null;
    private channel: ICometChannel | null = null;
    private wsFactory: WebSocketFactory | null = null;
    private isConnected = false;
    private drivePongTopic: string | null = null;

    /**
     * Invoked by the pong CometD subscription inside `initializeChannel()` whenever a
     * drive-pong is received. Registered via `registerPongHandler()` and automatically
     * re-applies on every reconnect since `initializeChannel()` always reads the current
     * value of this field.
     */
    private onPongReceivedCallback: (() => void) | null = null;

    /**
     * Invoked at the start of `tearDownChannel()` so any pending work tied to the
     * current channel (e.g. a pong-timeout timer) can be cancelled before the channel
     * reference is cleared. Without this, stale callbacks could fire after reconnect
     * and trigger a spurious second reconnect.
     *
     * Like `onPongReceivedCallback`, this survives teardown so it still applies after a
     * reconnect. `startSubscriptions()` is teardown-then-initialise, so clearing it here
     * would silently disarm the handler for every subsequent teardown — the registrant
     * has no way to observe that and re-register. See MYG-143588.
     */
    private onTearDownCallback: (() => void) | null = null;

    private ignoreAckCount = 0;
    private readonly ignoredDeviceIds: Map<string, number> = new Map();
    private totalIgnoredMessagesCount = 0;

    constructor (api: Api, user: string | null) {
        super();
        this.api = api;
        this.user = user;
        this.logger = new WebSocketLogger(Source.CHANNEL, this.user || "'anonymous'");

        this.initializeChannel();
    }

    isInitialized(): boolean {
        return !!this.channel && this.isConnected;
    }

    /**
     * Returns a diagnostic snapshot of the channel's connectivity state for logging.
     * "readyState" mirrors the native WebSocket concept: open when both the channel
     * object exists and isConnected is true, closed otherwise.
     */
    getChannelDiagnostics(): string {
        const readyState = this.isConnected && this.channel ? "open" : "closed";
        const transportConnected = this.wsFactory?.getConnection()?.isConnected() ?? null;
        return `readyState=${ readyState }, channel=${ this.channel ? "present" : "null" }, isConnected=${ this.isConnected }, transport.isConnected=${ transportConnected }`;
    }

    /**
     * Registers a callback to be invoked whenever a drive-pong is received on this channel.
     * The callback is automatically applied to every reconnect because `initializeChannel()`
     * always reads the current value of `onPongReceivedCallback`.
     */
    registerPongHandler(handler: () => void): void {
        if (this.onPongReceivedCallback !== null && this.onPongReceivedCallback !== handler) {
            this.logger.warn("registerPongHandler: overwriting an existing pong handler — only one handler is supported.");
        }
        this.onPongReceivedCallback = handler;
        this.subscribeToDrivePongTopic();
    }

    /**
     * Registers a callback to be invoked at the start of `tearDownChannel()` before the
     * channel is torn down and a new one is initialised.
     */
    registerTearDownHandler(handler: () => void): void {
        if (this.onTearDownCallback !== null && this.onTearDownCallback !== handler) {
            this.logger.warn("registerTearDownHandler: overwriting an existing teardown handler — only one handler is supported.");
        }
        this.onTearDownCallback = handler;
    }

    /**
     * Detaches a previously registered teardown handler. Pass the same handler reference
     * that was registered; a mismatch is ignored so one owner cannot detach another's
     * handler. Callers that own a handler for their lifetime (rather than the channel's)
     * must call this when they are destroyed, otherwise the channel keeps invoking a
     * callback belonging to a dead service.
     */
    unregisterTearDownHandler(handler: () => void): void {
        if (this.onTearDownCallback === handler) {
            this.onTearDownCallback = null;
        }
    }

    /**
     * Detaches a previously registered pong handler. Pass the same handler reference
     * that was registered; a mismatch is ignored so one owner cannot detach another's
     * handler. Callers that own a handler for their lifetime (rather than the channel's)
     * must call this when they are destroyed, otherwise the channel keeps a reference to
     * a callback belonging to a dead service, preventing GC.
     */
    unregisterPongHandler(handler: () => void): void {
        if (this.onPongReceivedCallback === handler) {
            this.onPongReceivedCallback = null;
        }
    }

    subscribe(topic: string, callback: any): void {
        if (!this.channel) {
            return;
        }
        if (this.channel.getSubscriptions().indexOf(topic) === -1) {
            this.channel.subscribe(topic, e => {
                this.logger.log(`Received notification for ${ topic }`);

                // Handle ACK for vehiclemove notifications
                if (this.shouldSendAck(topic)) {
                    this.handleAckRequiredMessage(topic, e);
                }

                callback(e);
            });
            this.logger.log(`Subscribing to ${ topic }`);
        } else {
            this.logger.log(`Already subscribed to ${ topic }`);
        }
    }

    getSubscriptions(): string[] {
        if (!this.channel) {
            return [];
        }
        return this.getPublicSubscriptions();
    }

    unsubscribe(topic: string, callback?: any): void {
        if (!this.channel) {
            return;
        }
        if (this.getPublicSubscriptions().indexOf(topic) !== -1) {
            this.channel.unsubscribe(topic).then(() => {
                if (this.channel && !this.getPublicSubscriptions().length) {
                    this.channel.shutdown().then(() => callback && callback());
                } else {
                    callback && callback();
                }
            });
            this.logger.log(`Unsubscribing from ${ topic }`);
        }
    }

    unsubscribeMultiple(topics: string[], callback?: any): void {
        if (!this.channel || !topics || topics.length === 0) {
            callback && callback();
            return;
        }

        // Filter to only topics that are actually subscribed
        const subscribedTopics = topics.filter(topic =>
            this.getPublicSubscriptions().indexOf(topic) !== -1);

        if (subscribedTopics.length === 0) {
            callback && callback();
            return;
        }

        this.logger.log(`Batch unsubscribing from ${ subscribedTopics.length } topics: ${ subscribedTopics.join(", ") }`);

        this.channel.unsubscribeMultiple(subscribedTopics).then(() => {
            if (this.channel && !this.getPublicSubscriptions().length) {
                this.channel.shutdown().then(() => callback && callback());
            } else {
                callback && callback();
            }
        }).catch((error) => {
            this.logger.error(`Batch unsubscribe failed: ${ error.message }`, error);
            callback && callback();
        });
    }

    startSubscriptions() {
        this.tearDownChannel();
        this.initializeChannel();
    }

    stopSubscriptions() {
        this.tearDownChannel();
    }

    /**
     * Public methods for debug page controls
     */

    getIgnoreAckCount(): number {
        return this.ignoreAckCount;
    }

    setIgnoreAckCount(count: number): void {
        this.ignoreAckCount = Math.max(0, count);
        logger.log(Source.WEBSOCKET_ACK, `ACK ignore count set to: ${ this.ignoreAckCount }`, this.user);
    }

    getIgnoredMessagesCount(): number {
        return this.totalIgnoredMessagesCount;
    }

    resetIgnoredMessagesCount(): void {
        this.totalIgnoredMessagesCount = 0;
        this.ignoredDeviceIds.clear();
        logger.log(Source.WEBSOCKET_ACK, "Reset ignored messages counter", this.user);
    }

    sendManualAck(deviceId: string): void {
        this.sendAckMessageDirect(deviceId, "VehicleMotionAck");
        this.totalIgnoredMessagesCount = 0;
        this.ignoredDeviceIds.delete(deviceId);
    }

    /**
     * Sends a "drive-ping" frame over the active WebSocket connection.
     * Pong receipt and timeout handling are managed externally via the handlers registered
     * with `registerPongHandler` and `registerTearDownHandler`.
     *
     * Throws if the channel is not connected so the caller can detect a dead socket
     * and trigger an immediate reconnect.
     */
    sendDrivePing(): void {
        if (!this.channel || !this.isConnected) {
            throw new Error("WebSocket channel not connected");
        }
        this.channel.send(DRIVE_PING_MESSAGE);
    }

    /**
     * Sends a "PING" string over the active WebSocket connection for liveness probing.
     * Throws if the channel is not connected so the caller can detect a dead socket
     * and trigger reconnect.
     */
    sendHeartbeat(): void {
        if (!this.channel || !this.isConnected) {
            throw new Error("WebSocket channel not connected");
        }
        this.channel.send("PING");
    }

    private tearDownChannel() {
        // Notify registered teardown handler before clearing the channel reference.
        // The handler is deliberately retained: startSubscriptions() tears down and
        // re-initialises, so clearing it would leave every reconnected channel without a
        // teardown handler while the registrant still believes it is registered.
        // Use unregisterTearDownHandler() to detach. See MYG-143588.
        this.onTearDownCallback?.();
        this.wsFactory = null;
        this.isConnected = false;
        this.drivePongTopic = null;
        if (this.channel) {
            this.channel.suspend();
            this.channel = null;
            this.logger.log("WebSocketChannel Teardown");
        }
    }

    private initializeChannel() {
        const credentials = this.api.getCurrentCredentials();
        const loginData = credentials && credentials.credentials;

        if (loginData) {
            const channelId = uuid();
            const onError = (e?: Error | String | string | IWebSocketChannelError) => {
                this.isConnected = false;
                if (!e || !this.logger) {
                    return;
                }
                // Generic WebSocket errors are expected behavior (network changes, backgrounding, sleep)
                // and don't contain useful information
                if ((e as IWebSocketChannelError).data) {
                    const errorWithData = e as IWebSocketChannelError;
                    // JSONRPC error from api.call error callback
                    this.logger.error(`Channel-Api: ${ errorWithData.name } ${ errorWithData.message } Id: ${ errorWithData.data?.id }`, errorWithData);
                }
                // Other errors- just leave it. When websocket channel receives onerror, the event doesn't contain useful information
                // Can come from network loss & channel shutdown
            };
            const onReconnect = () => {
                this.isConnected = true;
                dispatcher.trigger(DispatcherEvents.StatusOnline, true);
            };

            this.wsFactory = new WebSocketFactory(geotab.location.host, this.logger);
            const database = geotab.location.database;
            this.channel = CometChannel(database, channelId, this.wsFactory, new MyGeotabConnectionAdapter(this.api.driveApi), onError, this.logger, undefined, undefined, onReconnect);
            // Assume connected initially, onError will set to false if connection fails
            this.isConnected = true;

            // The topic is channel-scoped ("drive-pong-{channelId}") so the server
            // publishes the pong only to the connection that sent the ping. It is only
            // subscribed once the ping/pong service registers a handler, keeping the
            // subscription fully behind the feature flag that enables that service.
            this.drivePongTopic = `drive-pong-${ channelId }`;
            this.subscribeToDrivePongTopic();

            this.logger.log("WebSocketChannel Initialized");
        } else {
            this.logger.warn("Supplied api parameter doesn't have valid credentials. Channel won't be initialized");
        }
    }

    private getPublicSubscriptions(): string[] {
        if (!this.channel) {
            return [];
        }
        return this.channel.getSubscriptions().filter(topic => topic !== this.drivePongTopic);
    }

    private subscribeToDrivePongTopic(): void {
        if (!this.channel || !this.drivePongTopic) {
            return;
        }

        if (this.channel.getSubscriptions().indexOf(this.drivePongTopic) !== -1) {
            return;
        }

        // Subscribe eagerly during channel initialisation so the server-side
        // subscription is fully established well before the first drive-ping is sent.
        // The callback guard is intentionally absent here — `onPongReceivedCallback`
        // uses optional chaining, so arriving pongs are silently dropped until the
        // ping service registers its handler.  This eliminates the race where the
        // first ping is sent while the subscribe handshake is still in flight.
        this.channel.subscribe(this.drivePongTopic, () => {
            this.onPongReceivedCallback?.();
            this.logger.log("WebSocket drive-pong received");
        });
    }

    private shouldSendAck(topic: string): boolean {
        return topic.startsWith("vehiclemove|");
    }

    private handleAckRequiredMessage(topic: string, eventData: any): void {
        try {
            // Extract deviceId from topic: vehiclemove|database|deviceId
            const topicParts = topic.split("|");
            if (topicParts.length === 3 && topicParts[0] === "vehiclemove") {
                const deviceId = topicParts[2];
                logger.log(Source.WEBSOCKET_ACK, `Received vehiclemove notification for device: ${ deviceId }, data: ${ JSON.stringify(eventData) }`, this.user);
                this.sendAckMessage(deviceId);
            } else {
                logger.log(Source.WEBSOCKET_ACK, "WARN Invalid vehiclemove topic format: " + topic, this.user);
            }
        } catch (error: any) {
            logger.error(Source.WEBSOCKET_ACK, `Error handling ACK-required message:`, this.user, error);
        }
    }

    private sendAckMessage(deviceId: string, messageType = "VehicleMotionAck"): boolean {
        // Check if we should ignore this ACK to test retry logic
        if (this.ignoreAckCount > 0) {
            const currentIgnoreCount = this.ignoredDeviceIds.get(deviceId) || 0;
            if (currentIgnoreCount + 1 <= this.ignoreAckCount) {
                this.ignoredDeviceIds.set(deviceId, currentIgnoreCount + 1);
                this.totalIgnoredMessagesCount++;
                logger.log(Source.WEBSOCKET_ACK, `IGNORING ACK ${ currentIgnoreCount + 1 }/${ this.ignoreAckCount } for device ID: ${ deviceId } - testing retry logic`, this.user);
                return false; // Don't send ACK yet
            } else {
                logger.log(Source.WEBSOCKET_ACK, `Sending ACK on attempt ${ currentIgnoreCount + 1 } for device ID: ${ deviceId }`, this.user);
                // Clean up after we've sent it
                this.ignoredDeviceIds.delete(deviceId);
                // Reset the total counter for the next cycle
                this.totalIgnoredMessagesCount = 0;
            }
        }

        return this.sendAckMessageDirect(deviceId, messageType);
    }

    private sendAckMessageDirect(deviceId: string, messageType = "VehicleMotionAck"): boolean {
        const ackMessage = {
            Type: messageType,
            DeviceId: deviceId
        };

        try {
            const success = this.sendDirectMessage(ackMessage);
            if (success) {
                logger.log(Source.WEBSOCKET_ACK, `Sent ${ messageType } for device: ${ deviceId }`, this.user);
                return true;
            } else {
                logger.error(Source.WEBSOCKET_ACK, `Failed to send ${ messageType } for device: ${ deviceId }`, this.user);
                return false;
            }
        } catch (error: any) {
            logger.error(Source.WEBSOCKET_ACK, `Failed to send ${ messageType } for device: ${ deviceId } - ${ error.message }`, this.user, error);
            return false;
        }
    }

    /**
     * Send a direct message through the WebSocket connection for debug page
     */
    private sendDirectMessage(message: any): boolean {
        try {
            if (!this.channel || !this.isConnected) {
                throw new Error("Cannot send message: channel connection is not available");
            }
            this.channel.send(JSON.stringify(message));
            return true;
        } catch (error: any) {
            this.isConnected = false;
            throw error;
        }
    }
}
